import { ExportOutputFormat } from '@ais-ui/websockets';

export type MailActionType =
  | 'notify'
  | 'csvatt'
  | 'csvlink'
  | 'pdfatt'
  | 'pdflink'
  | 'excelatt'
  | 'excellink'
  | 'zipatt'
  | 'ziplink'
  | 'esrishpatt'
  | 'esrishplink'
  | 'esrirstatt'
  | 'esrirstlink';

export interface MailActionOption {
  value: MailActionType;
  label: string;
  exportFormat?: ExportOutputFormat;
  isAttachment?: boolean;
}

// Map from export format to attachment/link mail actions
// Only includes formats that support mail actions
const exportFormatToMailActions: Partial<
  Record<
    ExportOutputFormat,
    { attachment: MailActionType; link: MailActionType }
  >
> = {
  OF_PDF: { attachment: 'pdfatt', link: 'pdflink' },
  OF_CSV: { attachment: 'csvatt', link: 'csvlink' },
  OF_EXCEL: { attachment: 'excelatt', link: 'excellink' },
  OF_ZIP: { attachment: 'zipatt', link: 'ziplink' },
  OF_ESRI_SHAPES: { attachment: 'esrishpatt', link: 'esrishplink' },
  OF_ESRI_RASTER: { attachment: 'esrirstatt', link: 'esrirstlink' },
};

// Human-readable labels for export formats
const exportFormatLabels: Partial<Record<ExportOutputFormat, string>> = {
  OF_PDF: 'PDF',
  OF_CSV: 'CSV',
  OF_EXCEL: 'Excel',
  OF_ZIP: 'ZIP',
  OF_ESRI_SHAPES: 'ESRI Shapefile',
  OF_ESRI_RASTER: 'ESRI Raster',
};

/**
 * Given available export formats, returns the list of mail action options
 * that can be selected by the user.
 */
export function getMailActionOptions(
  exportFormats: ExportOutputFormat[] | undefined,
): MailActionOption[] {
  const options: MailActionOption[] = [
    { value: 'notify', label: 'Notification only' },
  ];

  if (!exportFormats) {
    return options;
  }

  for (const format of exportFormats) {
    const actions = exportFormatToMailActions[format];
    const label = exportFormatLabels[format] || format;

    if (actions) {
      options.push({
        value: actions.attachment,
        label: `${label} attachment`,
        exportFormat: format,
        isAttachment: true,
      });
      options.push({
        value: actions.link,
        label: `${label} link`,
        exportFormat: format,
        isAttachment: false,
      });
    }
  }

  return options;
}

/**
 * Returns the default mail action (notify if email provided, empty string otherwise)
 */
export function getDefaultMailAction(hasEmail: boolean): MailActionType | '' {
  return hasEmail ? 'notify' : '';
}
